/**
 * WhatsNewAdmin — release notes editor. Feeds the "What just shipped"
 * strip on the marketing landing page via `/api/public/whats-new`.
 * Admins can add / edit / publish-toggle / delete short update cards
 * without spamming user inboxes with broadcast notifications.
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Bell, Plus, Trash2, Eye, EyeOff, Save, X, Sparkles, Wrench, Bug, Flag } from 'lucide-react';

const CATEGORIES = [
  { key: 'shipped',  label: 'Shipped',  icon: Sparkles },
  { key: 'improved', label: 'Improved', icon: Wrench },
  { key: 'fix',      label: 'Fix',      icon: Bug },
  { key: 'planned',  label: 'Planned',  icon: Flag },
];

const EMPTY = { title: '', body: '', category: 'shipped', link: '', published: true };

const WhatsNewAdmin = () => {
  const { API } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/whats-new`);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load What\'s New items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [API]); // eslint-disable-line

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      title: it.title || '',
      body: it.body || '',
      category: it.category || 'shipped',
      link: it.link || '',
      published: it.published !== false,
    });
  };

  const cancelEdit = () => { setEditingId(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error('Title and body are required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/whats-new/${editingId}`, form);
        toast.success('Updated');
      } else {
        await axios.post(`${API}/admin/whats-new`, form);
        toast.success('Published');
      }
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (it) => {
    try {
      await axios.put(`${API}/admin/whats-new/${it.id}`, { published: !it.published });
      toast.success(it.published ? 'Unpublished' : 'Published');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Toggle failed'); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title}"? This is permanent.`)) return;
    try {
      await axios.delete(`${API}/admin/whats-new/${it.id}`);
      toast.success('Deleted');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const catMeta = (key) => CATEGORIES.find((c) => c.key === key) || CATEGORIES[0];

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="whats-new-admin">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight flex items-center gap-2">
          <Bell className="w-6 h-6 text-primary" /> What's New — Release Notes
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Post short "just shipped" cards that appear on the landing page in the <em>What just shipped</em> strip.
          These do not push notifications to users' inboxes — use <code>/admin/broadcast</code> for that.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{editingId ? 'Edit item' : 'Add a new item'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Shipped: bulk-cite pinned docs"
              data-testid="whats-new-title-input"
              maxLength={140}
            />
          </div>
          <div>
            <Label>Body</Label>
            <Textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="One or two sentences a user would understand."
              rows={3}
              data-testid="whats-new-body-input"
              maxLength={2000}
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {CATEGORIES.map((c) => {
                  const Ic = c.icon;
                  const active = form.category === c.key;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setForm({ ...form, category: c.key })}
                      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold border ${active ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground border-border hover:bg-muted'}`}
                      data-testid={`whats-new-cat-${c.key}`}
                    >
                      <Ic className="w-3 h-3" /> {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Link (optional)</Label>
              <Input
                value={form.link}
                onChange={(e) => setForm({ ...form, link: e.target.value })}
                placeholder="/lms or https://…"
                data-testid="whats-new-link-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <input
              id="whats-new-published"
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
              data-testid="whats-new-published-checkbox"
            />
            <label htmlFor="whats-new-published" className="text-sm font-medium">Published (visible on landing page)</label>
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={save} disabled={saving} data-testid="whats-new-save-btn">
              <Save className="w-4 h-4 mr-1.5" /> {editingId ? 'Update' : 'Publish'}
            </Button>
            {editingId && (
              <Button variant="outline" onClick={cancelEdit} data-testid="whats-new-cancel-btn">
                <X className="w-4 h-4 mr-1.5" /> Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div>
        <h2 className="font-heading text-xl font-extrabold tracking-tight mb-3">All items ({items.length})</h2>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-muted-foreground border rounded-lg p-6 text-center" data-testid="whats-new-empty">
            No items yet. Add your first update above.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="whats-new-list">
            {items.map((it) => {
              const cm = catMeta(it.category);
              const CmIc = cm.icon;
              return (
                <li key={it.id} className={`rounded-lg border p-4 ${it.published ? 'bg-background' : 'bg-muted/40 opacity-70'}`} data-testid={`whats-new-item-${it.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-[10px] uppercase tracking-widest font-bold">
                          <CmIc className="w-3 h-3 mr-1" /> {cm.label}
                        </Badge>
                        {!it.published && <Badge variant="secondary" className="text-[10px]">Draft</Badge>}
                        <span className="text-[11px] text-muted-foreground">{new Date(it.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                      <div className="font-heading font-extrabold text-sm mt-1">{it.title}</div>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{it.body}</p>
                      {it.link && <a href={it.link} className="text-xs text-primary underline mt-1 inline-block" target="_blank" rel="noreferrer">{it.link}</a>}
                    </div>
                    <div className="flex flex-shrink-0 gap-1">
                      <Button size="sm" variant="outline" onClick={() => togglePublished(it)} data-testid={`whats-new-toggle-${it.id}`}>
                        {it.published ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => startEdit(it)} data-testid={`whats-new-edit-${it.id}`}>Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => remove(it)} data-testid={`whats-new-delete-${it.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default WhatsNewAdmin;
